"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActorOptimizeQueryOperationFilterPushdown = void 0;
const bus_optimize_query_operation_1 = require("@comunica/bus-optimize-query-operation");
const context_entries_1 = require("@comunica/context-entries");
const core_1 = require("@comunica/core");
const utils_algebra_1 = require("@comunica/utils-algebra");
const utils_query_operation_1 = require("@comunica/utils-query-operation");
const rdf_terms_1 = require("rdf-terms");
/**
 * A comunica Filter Pushdown Optimize Query Operation Actor.
 */
class ActorOptimizeQueryOperationFilterPushdown extends bus_optimize_query_operation_1.ActorOptimizeQueryOperation {
    aggressivePushdown;
    maxIterations;
    splitConjunctive;
    mergeConjunctive;
    pushIntoLeftJoins;
    pushEqualityIntoPatterns;
    constructor(args) {
        super(args);
        this.aggressivePushdown = args.aggressivePushdown;
        this.maxIterations = args.maxIterations;
        this.splitConjunctive = args.splitConjunctive;
        this.mergeConjunctive = args.mergeConjunctive;
        this.pushIntoLeftJoins = args.pushIntoLeftJoins;
        this.pushEqualityIntoPatterns = args.pushEqualityIntoPatterns;
    }
    async test(_action) {
        return (0, core_1.passTestVoid)();
    }
    async run(action) {
        const dataFactory = action.context.getSafe(context_entries_1.KeysInitQuery.dataFactory);
        const algebraFactory = new utils_algebra_1.AlgebraFactory(dataFactory);
        let operation = action.operation;
        // Split conjunctive filters into nested filters
        if (this.splitConjunctive) {
            operation = utils_algebra_1.algebraUtils.mapOperation(operation, {
                [utils_algebra_1.Algebra.Types.FILTER]: { transform: (filterOp) => {
                        // Split conjunctive filters into separate filters
                        if ((0, utils_algebra_1.isKnownSubType)(filterOp.expression, utils_algebra_1.Algebra.ExpressionTypes.OPERATOR) &&
                            filterOp.expression.operator === '&&') {
                            this.logDebug(action.context, `Split conjunctive filter into ${filterOp.expression.args.length} nested filters`);
                            return filterOp.expression.args
                                .reduce((operation, expression) => algebraFactory.createFilter(operation, expression), filterOp.input);
                        }
                        return filterOp;
                    } },
            });
        }
        // Collect selector shapes of all operations
        const sources = this.getSources(operation);
        // eslint-disable-next-line ts/no-unnecessary-type-assertion
        const sourceShapes = new Map(await Promise.all(sources
            .map(async (source) => [
            source,
            await source.source.getSelectorShape(source.context ? action.context.merge(source.context) : action.context),
        ])));
        // Push down all filters
        // We loop until no more filters can be pushed down.
        let repeat = true;
        let iterations = 0;
        while (repeat && iterations < this.maxIterations) {
            repeat = false;
            operation = utils_algebra_1.algebraUtils.mapOperation(operation, {
                [utils_algebra_1.Algebra.Types.FILTER]: { transform: (filterOp) => {
                        // Check if the filter must be pushed down
                        const extensionFunctions = action.context.get(context_entries_1.KeysInitQuery.extensionFunctions);
                        const extensionFunctionsAlwaysPushdown = action.context.get(context_entries_1.KeysInitQuery.extensionFunctionsAlwaysPushdown);
                        if (!this.shouldAttemptPushDown(filterOp, sources, sourceShapes, extensionFunctions, extensionFunctionsAlwaysPushdown)) {
                            return filterOp;
                        }
                        // For all filter expressions in the operation,
                        // we attempt to push them down as deep as possible into the algebra.
                        const variables = (0, utils_query_operation_1.getExpressionVariables)(filterOp.expression);
                        const [isModified, result] = this
                            .filterPushdown(filterOp.expression, variables, filterOp.input, algebraFactory, action.context);
                        if (isModified) {
                            repeat = true;
                        }
                        return result;
                    } },
            });
            iterations++;
        }
        if (iterations > 1) {
            this.logDebug(action.context, `Pushed down filters in ${iterations} iterations`);
        }
        // Merge nested filters into conjunctive filters
        if (this.mergeConjunctive) {
            operation = utils_algebra_1.algebraUtils.mapOperation(operation, {
                [utils_algebra_1.Algebra.Types.FILTER]: { transform: (op) => {
                        if (op.input.type === utils_algebra_1.Algebra.Types.FILTER) {
                            const { nestedExpressions, input } = this.getNestedFilterExpressions(op);
                            this.logDebug(action.context, `Merge ${nestedExpressions.length} nested filters into conjunctive filter`);
                            return algebraFactory.createFilter(input, nestedExpressions.slice(1).reduce((previous, current) => algebraFactory.createOperatorExpression('&&', [previous, current]), nestedExpressions[0]));
                        }
                        return op;
                    } },
            });
        }
        return { operation, context: action.context };
    }
    /**
     * Check if the given filter operation must be attempted to push down, based on the following criteria:
     * - Always push down if aggressive mode is enabled
     * - Push down if the filter is extremely selective
     * - Don't push down extension functions comunica support, but a source does not
     * - Push down if federated and at least one accepts the filter
     * @param operation The filter operation
     * @param sources The query sources in the operation
     * @param sourceShapes A mapping of sources to selector shapes.
     * @param extensionFunctions The extension functions comunica supports.
     * @param extensionFunctionsAlwaysPushdown If extension functions must always be pushed down to sources that support
     *                                         expressions, even if those sources to not explicitly declare support for
     *                                         these extension functions.
     */
    shouldAttemptPushDown(operation, sources, sourceShapes, extensionFunctions, extensionFunctionsAlwaysPushdown) {
        // Always push down if aggressive mode is enabled
        if (this.aggressivePushdown) {
            return true;
        }
        // Push down if the filter is extremely selective
        const expression = operation.expression;
        if ((0, utils_algebra_1.isKnownSubType)(expression, utils_algebra_1.Algebra.ExpressionTypes.OPERATOR) && expression.operator === '=' &&
            (((0, utils_algebra_1.isKnownSubType)(expression.args[0], utils_algebra_1.Algebra.ExpressionTypes.TERM) &&
                expression.args[0].term.termType !== 'Variable' &&
                (0, utils_algebra_1.isKnownSubType)(expression.args[1], utils_algebra_1.Algebra.ExpressionTypes.TERM) &&
                expression.args[1].term.termType === 'Variable') ||
                ((0, utils_algebra_1.isKnownSubType)(expression.args[0], utils_algebra_1.Algebra.ExpressionTypes.TERM) &&
                    expression.args[0].term.termType === 'Variable' &&
                    (0, utils_algebra_1.isKnownSubType)(expression.args[1], utils_algebra_1.Algebra.ExpressionTypes.TERM) &&
                    expression.args[1].term.termType !== 'Variable'))) {
            return true;
        }
        // Don't push down extension functions comunica support, but no source does
        if (extensionFunctions && (0, utils_algebra_1.isKnownSubType)(expression, utils_algebra_1.Algebra.ExpressionTypes.NAMED) &&
            expression.name.value in extensionFunctions &&
            // Checks if there's not a single source that supports the extension function
            !sources.some(source => (0, utils_query_operation_1.doesShapeAcceptOperation)(sourceShapes.get(source), expression))) {
            return false;
        }
        // Push down if federated and at least one accepts the filter
        if (sources.some(source => (0, utils_query_operation_1.doesShapeAcceptOperation)(sourceShapes.get(source), operation, {
            wildcardAcceptAllExtensionFunctions: extensionFunctionsAlwaysPushdown,
        }))) {
            return true;
        }
        // Don't push down in all other cases
        return false;
    }
    /**
     * Collected all sources that are defined within the given operation of children recursively.
     * @param operation An operation.
     */
    getSources(operation) {
        const sources = new Set();
        const sourceAdder = (subOperation) => {
            const src = (0, utils_query_operation_1.getOperationSource)(subOperation);
            if (src) {
                sources.add(src);
            }
            return false;
        };
        utils_algebra_1.algebraUtils.visitOperation(operation, {
            [utils_algebra_1.Algebra.Types.PATTERN]: { visitor: sourceAdder },
            [utils_algebra_1.Algebra.Types.SERVICE]: { visitor: sourceAdder },
            [utils_algebra_1.Algebra.Types.LINK]: { visitor: sourceAdder },
            [utils_algebra_1.Algebra.Types.NPS]: { visitor: sourceAdder },
        });
        return [...sources];
    }
    getOverlappingOperations(operation, expressionVariables) {
        const fullyOverlapping = [];
        const partiallyOverlapping = [];
        const notOverlapping = [];
        for (const input of operation.input) {
            const inputVariables = utils_algebra_1.algebraUtils.inScopeVariables(input);
            if (this.variablesSubSetOf(expressionVariables, inputVariables)) {
                fullyOverlapping.push(input);
            }
            else if (this.variablesIntersect(expressionVariables, inputVariables)) {
                partiallyOverlapping.push(input);
            }
            else {
                notOverlapping.push(input);
            }
        }
        return {
            fullyOverlapping,
            partiallyOverlapping,
            notOverlapping,
        };
    }
    /**
     * Recursively push down the given expression into the given operation if possible.
     * Different operators have different semantics for choosing whether or not to push down,
     * and how this pushdown is done.
     * For every passed operator, it is checked whether or not the filter will have any effect on the operation.
     * If not, the filter is voided.
     * @param expression An expression to push down.
     * @param expressionVariables The variables inside the given expression.
     * @param operation The operation to push down into.
     * @param factory An algebra factory.
     * @param context The action context.
     * @return A tuple indicating if the operation was modified and the modified operation.
     */
    filterPushdown(expression, expressionVariables, operation, factory, context) {
        // Void false expressions
        if (this.isExpressionFalse(expression)) {
            return [true, factory.createUnion([])];
        }
        // Don't push down (NOT) EXISTS
        if ((0, utils_algebra_1.isKnownOperation)(expression, utils_algebra_1.Algebra.Types.EXPRESSION, utils_algebra_1.Algebra.ExpressionTypes.EXISTENCE)) {
            return [false, factory.createFilter(operation, expression)];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.EXTEND)) {
            // Pass if the variable is not part of the expression
            if (!this.variablesIntersect([operation.variable], expressionVariables)) {
                return [true, factory.createExtend(this.filterPushdown(expression, expressionVariables, operation.input, factory, context)[1], operation.variable, operation.expression)];
            }
            return [false, factory.createFilter(operation, expression)];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.FILTER)) {
            // Always pass
            const [isModified, result] = this
                .filterPushdown(expression, expressionVariables, operation.input, factory, context);
            return [isModified, factory.createFilter(result, operation.expression)];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.JOIN)) {
            // Don't push down for empty join
            if (operation.input.length === 0) {
                return [false, factory.createFilter(operation, expression)];
            }
            // Determine overlapping operations
            const { fullyOverlapping, partiallyOverlapping, notOverlapping, } = this.getOverlappingOperations(operation, expressionVariables);
            const joins = [];
            let isModified = false;
            if (fullyOverlapping.length > 0) {
                isModified = true;
                joins.push(factory.createJoin(fullyOverlapping
                    .map(input => this.filterPushdown(expression, expressionVariables, input, factory, context)[1])));
            }
            if (partiallyOverlapping.length > 0) {
                joins.push(factory.createFilter(factory.createJoin(partiallyOverlapping, false), expression));
            }
            if (notOverlapping.length > 0) {
                joins.push(...notOverlapping);
            }
            if (joins.length > 1) {
                isModified = true;
            }
            if (isModified) {
                this.logDebug(context, `Push down filter across join entries with ${fullyOverlapping.length} fully overlapping, ${partiallyOverlapping.length} partially overlapping, and ${notOverlapping.length} not overlapping`);
            }
            return [isModified, joins.length === 1 ? joins[0] : factory.createJoin(joins)];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.NOP)) {
            return [true, operation];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.PROJECT)) {
            // Push down if variables overlap
            if (this.variablesIntersect(operation.variables, expressionVariables)) {
                return [true, factory.createProject(this.filterPushdown(expression, expressionVariables, operation.input, factory, context)[1], operation.variables)];
            }
            // Void expression otherwise
            return [true, operation];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.UNION)) {
            // Determine overlapping operations
            const { fullyOverlapping, partiallyOverlapping, notOverlapping, } = this.getOverlappingOperations(operation, expressionVariables);
            const unions = [];
            let isModified = false;
            if (fullyOverlapping.length > 0) {
                isModified = true;
                unions.push(factory.createUnion(fullyOverlapping
                    .map(input => this.filterPushdown(expression, expressionVariables, input, factory, context)[1])));
            }
            if (partiallyOverlapping.length > 0) {
                unions.push(factory.createFilter(factory.createUnion(partiallyOverlapping, false), expression));
            }
            if (notOverlapping.length > 0) {
                unions.push(...notOverlapping);
            }
            if (unions.length > 1) {
                isModified = true;
            }
            if (isModified) {
                this.logDebug(context, `Push down filter across union entries with ${fullyOverlapping.length} fully overlapping, ${partiallyOverlapping.length} partially overlapping, and ${notOverlapping.length} not overlapping`);
            }
            return [isModified, unions.length === 1 ? unions[0] : factory.createUnion(unions)];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.VALUES)) {
            // Only keep filter if it overlaps with the variables
            if (this.variablesIntersect(operation.variables, expressionVariables)) {
                return [false, factory.createFilter(operation, expression)];
            }
            return [true, operation];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.LEFT_JOIN)) {
            if (this.pushIntoLeftJoins) {
                const rightVariables = utils_algebra_1.algebraUtils.inScopeVariables(operation.input[1]);
                if (!this.variablesIntersect(expressionVariables, rightVariables)) {
                    // If filter *only* applies to left entry of optional, push it down into that.
                    this.logDebug(context, `Push down filter into left join`);
                    return [true, factory.createLeftJoin(this.filterPushdown(expression, expressionVariables, operation.input[0], factory, context)[1], operation.input[1], operation.expression)];
                }
            }
            // Don't push down in all other cases
            return [false, factory.createFilter(operation, expression)];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.PATTERN)) {
            if (this.pushEqualityIntoPatterns) {
                // Try to push simple FILTER(?s = <iri>) expressions into the pattern
                const pushableResult = this.getEqualityExpressionPushableIntoPattern(expression);
                if (pushableResult) {
                    let isModified = false;
                    const originalMetadata = operation.metadata;
                    operation = (0, rdf_terms_1.mapTermsNested)(operation, (value) => {
                        if (value.equals(pushableResult.variable)) {
                            isModified = true;
                            return pushableResult.term;
                        }
                        return value;
                    });
                    operation.type = utils_algebra_1.Algebra.Types.PATTERN;
                    operation.metadata = originalMetadata;
                    if (isModified) {
                        this.logDebug(context, `Push down filter into pattern for ?${pushableResult.variable.value}`);
                        return [true, factory.createJoin([
                                operation,
                                factory.createValues([pushableResult.variable], [{ [pushableResult.variable.value]: pushableResult.term }]),
                            ])];
                    }
                }
            }
            // Don't push down in all other cases
            return [false, factory.createFilter(operation, expression)];
        }
        if ((0, utils_algebra_1.isKnownOperation)(operation, utils_algebra_1.Algebra.Types.PATH)) {
            if (this.pushEqualityIntoPatterns) {
                // Try to push simple FILTER(?s = <iri>) expressions into the path
                const pushableResult = this.getEqualityExpressionPushableIntoPattern(expression);
                if (pushableResult &&
                    (operation.subject.equals(pushableResult.variable) || operation.object.equals(pushableResult.variable))) {
                    this.logDebug(context, `Push down filter into path for ?${pushableResult.variable.value}`);
                    const originalMetadata = operation.metadata;
                    operation = factory.createPath(operation.subject.equals(pushableResult.variable) ? pushableResult.term : operation.subject, operation.predicate, operation.object.equals(pushableResult.variable) ? pushableResult.term : operation.object);
                    operation.metadata = originalMetadata;
                    return [true, factory.createJoin([
                            operation,
                            factory.createValues([pushableResult.variable], [{ [pushableResult.variable.value]: pushableResult.term }]),
                        ])];
                }
            }
            // Don't push down in all other cases
            return [false, factory.createFilter(operation, expression)];
        }
        // Operations that do not support pushing down
        // Left-join and minus might be possible to support in the future.
        return [false, factory.createFilter(operation, expression)];
    }
    /**
     * Check if the given expression is a simple equals operation with one variable and one non-literal
     * (or literal with canonical lexical form) term that can be pushed into a pattern.
     * @param expression The current expression.
     * @return The variable and term to fill into the pattern, or undefined.
     */
    getEqualityExpressionPushableIntoPattern(expression) {
        if ((0, utils_algebra_1.isKnownSubType)(expression, utils_algebra_1.Algebra.ExpressionTypes.OPERATOR) && expression.operator === '=') {
            const arg0 = expression.args[0];
            const arg1 = expression.args[1];
            if ((0, utils_algebra_1.isKnownSubType)(arg0, utils_algebra_1.Algebra.ExpressionTypes.TERM) && arg0.term.termType !== 'Variable' &&
                (arg0.term.termType !== 'Literal' || this.isLiteralWithCanonicalLexicalForm(arg0.term)) &&
                (0, utils_algebra_1.isKnownSubType)(arg1, utils_algebra_1.Algebra.ExpressionTypes.TERM) &&
                arg1.term.termType === 'Variable') {
                return {
                    variable: arg1.term,
                    term: arg0.term,
                };
            }
            if ((0, utils_algebra_1.isKnownSubType)(arg0, utils_algebra_1.Algebra.ExpressionTypes.TERM) && arg0.term.termType === 'Variable' &&
                (0, utils_algebra_1.isKnownSubType)(arg1, utils_algebra_1.Algebra.ExpressionTypes.TERM) && arg1.term.termType !== 'Variable' &&
                (arg1.term.termType !== 'Literal' || this.isLiteralWithCanonicalLexicalForm(arg1.term))) {
                return {
                    variable: arg0.term,
                    term: arg1.term,
                };
            }
        }
    }
    /**
     * Check if the given term is a literal with datatype that where all values
     * can only have one possible lexical representation.
     * In other words, no variants of values exist that should be considered equal.
     * For example: "01"^xsd:number and "1"^xsd:number will return false.
     * @param term An RDF term.
     * @protected
     */
    isLiteralWithCanonicalLexicalForm(term) {
        switch (term.datatype.value) {
            case 'http://www.w3.org/2001/XMLSchema#string':
            case 'http://www.w3.org/1999/02/22-rdf-syntax-ns#langString':
            case 'http://www.w3.org/2001/XMLSchema#normalizedString':
            case 'http://www.w3.org/2001/XMLSchema#anyURI':
            case 'http://www.w3.org/2001/XMLSchema#base64Binary':
            case 'http://www.w3.org/2001/XMLSchema#language':
            case 'http://www.w3.org/2001/XMLSchema#Name':
            case 'http://www.w3.org/2001/XMLSchema#NCName':
            case 'http://www.w3.org/2001/XMLSchema#NMTOKEN':
            case 'http://www.w3.org/2001/XMLSchema#token':
            case 'http://www.w3.org/2001/XMLSchema#hexBinary':
                return true;
        }
        return false;
    }
    /**
     * Check if there is an overlap between the two given lists of variables.
     * @param varsA A list of variables.
     * @param varsB A list of variables.
     */
    variablesIntersect(varsA, varsB) {
        return varsA.some(varA => varsB.some(varB => varA.equals(varB)));
    }
    /**
     * Check if all variables from the first list are included in the second list.
     * The second list may contain other variables as well.
     * @param varsNeedles A list of variables to search for.
     * @param varsHaystack A list of variables to search in.
     */
    variablesSubSetOf(varsNeedles, varsHaystack) {
        return varsNeedles.length <= varsHaystack.length &&
            varsNeedles.every(varA => varsHaystack.some(varB => varA.equals(varB)));
    }
    /**
     * Check if an expression is simply 'false'.
     * @param expression An expression.
     */
    isExpressionFalse(expression) {
        const casted = expression;
        return (casted.term && casted.term.termType === 'Literal' && casted.term.value === 'false');
    }
    /**
     * Get all directly nested filter expressions.
     * As soon as a non-filter is found, it is returned as the input field.
     * @param op A filter expression.
     */
    getNestedFilterExpressions(op) {
        if ((0, utils_algebra_1.isKnownOperation)(op.input, utils_algebra_1.Algebra.Types.FILTER)) {
            const childData = this.getNestedFilterExpressions(op.input);
            return { nestedExpressions: [op.expression, ...childData.nestedExpressions], input: childData.input };
        }
        return { nestedExpressions: [op.expression], input: op.input };
    }
}
exports.ActorOptimizeQueryOperationFilterPushdown = ActorOptimizeQueryOperationFilterPushdown;
//# sourceMappingURL=ActorOptimizeQueryOperationFilterPushdown.js.map